/**
 * StorySplat Viewer v2
 *
 * PlayCanvas-based 3D viewer for StorySplat scenes.
 *
 * HTML Generation (CDN-based):
 *   Generate standalone HTML files that load the viewer from CDN.
 *   Usage: generateHTML(sceneData) or generateHTMLFromUrl(url)
 *
 * Dynamic Embedding:
 *   Create embedded viewers from scene data at runtime.
 *   Usage: createViewer(container, sceneData) or createViewerFromUrl(container, url)
 */

// ============================================
// HTML Generation (v2 - CDN-based, simple bootstrap)
// ============================================
export { generateHTML, generateHTMLFromUrl } from './html-generation/generateHTML';
export type { GenerateHTMLOptions } from './html-generation/generateHTML';

// ============================================
// MODE 2: Dynamic Viewer (for embedding)
// ============================================

// Primary API - recommended for embedding StorySplat scenes
// Handles fetching scene data AND automatic view/bandwidth tracking
export {
  createViewerFromSceneId,
  fetchSceneMeta,
  SceneNotFoundError,
  SceneApiError
} from './dynamic-viewer/createViewerFromSceneId';

// Low-level API - for advanced/internal use only
// - createViewer: Create viewer from scene data object (no tracking)
// - createViewerFromUrl: Create viewer from JSON URL (no tracking, internal use)
// For StorySplat scenes, use createViewerFromSceneId instead
export { createViewer, createViewerFromUrl, LOD_PRESETS, getEffectiveLodSettings, resolveLodSettings } from './dynamic-viewer/createViewer';
export type { LodPresetName, ResolvedLodSettings, ResolveLodSettingsOptions } from './dynamic-viewer/createViewer';
// VR mode: a visitor's switch that makes every scene ready for a PC VR headset (see ViewerOptions.vr).
export { getVrMode, setVrMode, vrModeAvailable, isVrHeadsetBrowser } from './dynamic-viewer/createViewer';
export { parseStereoParam, STEREO_FORMATS } from './dynamic-viewer/stereoMath';
export type { FocusConfig, FocusRegion } from './effects/focus-regions';
export type { AreaShape, AreaShapeKind, AreaApply } from './effects/area-shape';
export { convertAreaShape, outlineAreaPatch, outlineBoundsRegion, resolveArea } from './effects/area-shape';
export type {
  AnnotationLayersConfig,
  AnnotationLayerGroup,
  AnnotationShape,
  AnnotationPoint,
  AnnotationLayersPanelConfig,
} from './effects/annotation-layers';
export {
  resolveAnnotationLayers,
  annotationShapeDefaults,
  DEFAULT_ANNOTATION_COLORS,
  DEFAULT_ANNOTATION_OPACITY,
  MAX_ANNOTATION_GROUPS,
  MAX_ANNOTATION_SHAPES,
  MAX_ANNOTATION_SHAPE_POINTS,
  MAX_ANNOTATION_TOTAL_POINTS,
  MAX_ANNOTATION_REGION_SHAPES,
  MAX_ANNOTATION_OBJECT_SHAPES,
} from './effects/annotation-layers';
// Regions: one feature for highlights and annotation layers (replaces focusRegions + annotationLayers).
export type { RegionsConfig, SceneRegion, RegionArea, RegionDim, RegionsMenuConfig } from './effects/regions';
export type { HotspotFolder, HotspotFoldersConfig, VisitorDirectoryConfig } from './dynamic-viewer/hotspotFolders';
export { folderPath, folderSubtreeIds, folderTreeOrder, groupHotspotsByFolder, orderHotspotsByFolder, sanitizeHotspotFolders, sanitizeVisitorDirectory, DEFAULT_DIRECTORY_TITLE } from './dynamic-viewer/hotspotFolders';
export {
  regionsFromLegacy,
  legacyFromRegions,
  migrateRegionAnimations,
  newRegion,
  focusAreaId,
  regionAnimatableVolume,
  MAX_REGIONS,
  MAX_REGION_AREAS,
  MAX_REGION_VOLUMES,
  MAX_REGION_OBJECT_AREAS,
  MAX_REGION_OUTLINE_POINTS,
  MAX_REGION_POINTS_PER_OUTLINE,
  REGION_FADE_MS,
  REGION_PALETTE,
  DEFAULT_REGION_COLOR,
  DEFAULT_COLOR_STRENGTH,
  DEFAULT_REGION_DIM,
  DEFAULT_MENU_ALL_LABEL,
} from './effects/regions';
export { resolveRegions } from './effects/regions-effect';
export type { ResolvedRegions, ResolvedRegion, ResolvedRegionArea, ResolvedRegionsMenu } from './effects/regions-effect';
export { MAIN_SPLAT_OBJECT_ID, MAX_SPLAT_OBJECT_IDS, MAX_SPLAT_OBJECT_SLOTS, normalizeSplatObjectIds } from './effects/splat-objects';
export { fitSurfacePolygon, fitSurfaceOutline, surfacePolygonArea, formatArea, formatDistance, formatMeasurementNumber, convertArea, convertDistance, PLANAR_TOLERANCE } from './utils/surfacePolygon';
export type { SurfaceOutlineFit } from './utils/surfacePolygon';
export type { SurfacePoint, SurfacePolygon, MeasurementUnit } from './utils/surfacePolygon';
export { normalizeMeasurementSets, MeasurementSetsState, measurementShown, OTHER_MEASUREMENTS_SET_ID, OTHER_MEASUREMENTS_LABEL } from './utils/measurementSets';
export { MEASUREMENTS_SECTION_TITLE, SHOW_ALL_MEASUREMENTS_LABEL } from './dynamic-viewer/measurementSetsPicker';
export { OverlayController, TriggerController } from './dynamic-viewer/overlays';
// Read-only exact preview of the slide-out scene menu (editor "Preview menu").
export { renderSceneMenuPreview } from './dynamic-viewer/viewerUI';
// Notice for hosts that swap a failed scene for its legacy export iframe.
export { showLegacyViewerBanner } from './dynamic-viewer/viewerUI';

// Analytics - for consumers who want manual control over session tracking
export { setupAnalyticsTracking } from './dynamic-viewer/SessionRecorder';
export { setupGoogleAnalytics } from './dynamic-viewer/GoogleAnalytics';

// ============================================
// 4DGS Frame Sequence Player
// ============================================
export { FrameSequencePlayer } from './dynamic-viewer/FrameSequencePlayer';
export type { FrameSequenceConfig, FrameSequencePlayerOptions } from './dynamic-viewer/FrameSequencePlayer';

// ============================================
// Shared Utilities
// ============================================
export { transformSceneToExportProps, exportPropsToViewerConfig } from './transformers/sceneToConfig';
export {
  addVec3,
  cloneVec3,
  generateWaypointPathSamples,
  getPathSegmentCount,
  getSegmentHandles,
  getSegmentIndices,
  getVirtualBezierHandles,
  isBezierSegment,
  isLoopingMode,
  sampleCatmullRom,
  sampleCubicBezier,
  sampleWaypointPathSegment,
  scaleVec3,
  subVec3,
} from './utils/waypointPath';
export type {
  OverlayData,
  OverlayTrigger,
  OverlayAnchor,
  SceneTrigger,
} from './types';
export { createCameraPathSampler, timelineToPathProgress } from './utils/cameraPathSampler';
export { waypointStepTarget } from './dynamic-viewer/extraWaypoints';
export type {
  CameraPathLoopMode,
  CameraPathSampler,
  CameraPathSamplerOptions,
  CameraPathTiming,
} from './utils/cameraPathSampler';
export type {
  SegmentHandles,
  SegmentIndices,
  Vector3Like,
  WaypointCurveMode,
  WaypointCurveOut,
  WaypointPathNode,
  WaypointPathSample,
} from './utils/waypointPath';

// ============================================
// Types
// ============================================
export type {
  ExportProps,
  TemplateType,
  SceneData,
  WaypointData,
  ExtraWaypointData,
  HotspotData,
  HotspotOrbitView,
  HotspotContentBlock,
  LinkedAction,
  LinkedActionTarget,
  LinkedActionTargetKind,
  LinkedActionType,
  LinkedHotspotActionType,
  LinkedModelActionType,
  LinkedEntityAnimationActionType,
  CutawayAction,
  SplatSwapAction,
  CutawayOptions,
  CutawayStatus,
  MarkerLayer,
  MarkerLayerKind,
  ViewerInstance,
  RegionInfo,
  HotspotFolderInfo,
  VisitorDirectoryInfo,
  RegionEditorOverlay,
  MeasurementData,
  AreaMeasurementData,
  MeasurementSetData,
  MeasurementSetInfo,
  StereoFormat,
  StereoOptions,
  CameraPose,
  CameraPoseFollower,
  FrameCaptureOptions,
  FrameCaptureFrameOptions,
  FrameCaptureSession,
  PathRenderInfo,
  OnDemandRenderStats,
  ViewerOnDemandRenderStats,
  EditorViewerInstance,
  ViewerOptions,
  ViewerEvent,
  ViewerFromSceneIdOptions,
  ViewerLoadedData,
  LightConfig,
  ParticleConfig,
  CustomMeshConfig,
  ButtonLabels,
  ViewerButtonLabels,
  ViewerTheme,
  UILayout,
  UIElementId,
  ElementPosition,
  LayoutAnchor,
  OrbitCameraSettingsConfig,
  OrbitRotationLimitsConfig,
  OrbitZoomLimitsConfig,
  SplatRelightingConfig,
  LodSettingsConfig,
  EntityAnimationConfig,
  AnimationKeyframeConfig,
  AnimationTrackConfig,
  AnimationEasingType,
  AnimationPlaybackMode,
  AnimationTimelineMode,
  AnimatableEntityType,
  AnimatableProperty,
  SceneMenuLink,
  SceneGuideConfig,
  PostProcessingConfig,
  MirrorPlaneData,
  WaterPlaneData,
  PlanarFootprintVertex,
  CloudLayerData,
  CloudPreset,
  CloudQuality,
  ScanSkySettings,
} from './types';

// Procedural clouds: presets and defaults (the editor's Clouds panel uses them)
export {
  CLOUD_PRESETS,
  CLOUD_PRESET_LABELS,
  CLOUD_PRESET_ORDER,
  resolveCloudLook,
  cloudPresetPatch,
  defaultCloudLayerFor,
  randomCloudSeed,
  selectCloudTier,
  cloudLayerBudget,
  CLOUD_TIER_BUDGETS,
} from './effects/clouds/cloudParams';

// 8th Wall AR Integration
export { loadXR8, isXR8Available, isNativeWebXRAvailable } from './dynamic-viewer/eighthwall';
export { ARPlacement } from './dynamic-viewer/eighthwall';
export type { XR8PipelineConfig, ARPlacementConfig } from './dynamic-viewer/eighthwall';

// Button label defaults (for i18n reference)
export { DEFAULT_BUTTON_LABELS, generateViewerStyles } from './dynamic-viewer/viewerUI';

// Reveal effect types
export type { RevealPreset, RevealPresetConfig } from './effects';
export { REVEAL_PRESETS, getRevealPreset } from './effects';

// Splat relighting effect
export { GsplatRelighting, getGsplatRelightingClass } from './effects';
// Shadows: settings, the migration of pre-overhaul scenes and the device tier (shared with the editor)
export {
    SHADOW_DEFAULTS,
    SHADOW_MAP_SIZES,
    SHADOWS_VERSION,
    MAX_SPLAT_SHADOW_LIGHTS,
    resolveSceneShadows,
    migrateLegacyShadowData,
    resolveShadowTier,
    normalizeShadowMapSize,
    lightTypeCanCastShadows,
} from './effects/shadows/shadowSettings';
export type {
    ShadowMobileMode,
    SceneShadowFields,
    LightShadowFields,
    ResolvedSceneShadows,
    ResolvedLightShadow,
    ShadowTier,
    ShadowTierInput,
} from './effects/shadows/shadowSettings';
export { lightRotationFromDirection, forwardRotationFromLight, lightRotationFromForward } from './effects/shadows/lightDirection';

// ============================================
// Editor Extensions (for StorySplat editor app)
// ============================================
export { GizmoManager } from './editor/GizmoManager';
export type { GizmoMode, GizmoManagerConfig, TransformEvent } from './editor/GizmoManager';

export { SelectionManager } from './editor/SelectionManager';
export type { SelectionEvent, SelectionManagerConfig } from './editor/SelectionManager';

export { EditorCameraController } from './editor/EditorCameraController';
export type { EditorCameraControllerConfig } from './editor/EditorCameraController';

// Custom Script System (for editor live preview)
export { CustomScriptSystem, setupCustomScript } from './dynamic-viewer/CustomScriptSystem';
export type { CustomScriptAPI } from './dynamic-viewer/CustomScriptSystem';

// Camera Controls (reusable in editor)
export { CameraControls } from './dynamic-viewer/CameraControls';
export type { CameraControlsConfig } from './dynamic-viewer/CameraControls';

// Hidden FPV drone flight mode
export { DroneController } from './dynamic-viewer/DroneController';
export type { DroneControllerConfig } from './dynamic-viewer/DroneController';

// Build version (injected at build time by rollup-plugin-replace)
export const BUILD_VERSION: string = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : 'dev';
declare const __BUILD_TIMESTAMP__: string;

// Package semver (injected at build time by rollup-plugin-replace) — e.g. "2.9.85".
// BUILD_VERSION above is a human build timestamp; this is the npm version consumers pin to.
export const VIEWER_VERSION: string = typeof __PACKAGE_VERSION__ !== 'undefined' ? __PACKAGE_VERSION__ : 'dev';
declare const __PACKAGE_VERSION__: string;

// Entity Animation System
export { EntityAnimationSystem } from './dynamic-viewer/EntityAnimationSystem';

// Voxel Collision (for editor debug visualization)
export { VoxelCollision } from './dynamic-viewer/VoxelCollision';
export type { VoxelOctreeMetadata, PushOut } from './dynamic-viewer/VoxelCollision';

// Splat Edit System (for inline splat-level editing)
export {
    SplatEditSystem, SplatEditModel, SplatState, EditHistory,
    bakeEdits, encodePly, encodeSplat, parsePly, decodeGsplatResource, loadLodSource,
    estimateEditMemory, BASIC_PROPS, ADVANCED_PROPS, DEFAULT_DISPLAY,
} from './editor/splat-edit';
export type {
    SplatEditStats, SplatEditChange, EditOp, BakedSplats, BakeOptions, SplatPropId,
    HistogramResult, EditDisplayOptions, GradeParams, SelectionOp, CloneStrokeOptions, FloaterOptions,
} from './editor/splat-edit';

export type { HazeConfig, HazeRegion } from './effects/haze-regions';

// Entity animation catalog (easing curves + animatable properties per target type)
export {
  ANIMATION_EASINGS,
  SCENE_ANIMATION_TARGETS,
  applyAnimationEasing,
  getAnimationPropertyGroups,
  findAnimationPropertyGroup,
  isSceneAnimationTarget,
  sceneAnimationTargetId,
  isStepAnimationProperty,
  hexToAnimationRgb,
  animationRgbToHex,
  closestEquivalentRotation,
} from './dynamic-viewer/animationProperties';
export type {
  AnimationEasingInfo,
  AnimationPropertyGroup,
  AnimationPropertyKind,
  AnimationTargetContext,
} from './dynamic-viewer/animationProperties';
export type {
  EntityAnimationInfo,
  EntityAnimationPlaybackEvent,
  EntityPresentationHandlers,
  SceneAnimationHandler,
  SceneAnimationHandlers,
} from './dynamic-viewer/EntityAnimationSystem';

export { sampleSubjectCenters, buildSubjectCandidates } from './utils/subjectBounds';
export type { SubjectCandidate, SubjectVector } from './utils/subjectBounds';
export { findSubjectCamera } from './editor/subjectCamera';
export type { SubjectCameraSuggestion, SubjectCameraOptions } from './editor/subjectCamera';
